import { create } from 'zustand';
import { Activity } from '../types/activity';
import {
  getActivities,
  updateActivityStatus,
  acceptActivity,
  declineActivity,
  rateActivity
} from '../lib/supabase/activities';

interface ActivityState {
  activities: Activity[];
  isLoading: boolean;
  error: string | null;
  lastFetched: number | null;

  fetchActivities: () => Promise<void>;
  updateActivity: (activityId: string, updates: Partial<Activity>) => Promise<void>;
  acceptActivityRequest: (activityId: string) => Promise<void>;
  declineActivityRequest: (activityId: string, reason?: string) => Promise<void>;
    rateActivityRequest: (
    activityId: string,
    ratings: {
      punctuality:   number;
      sportsmanship: number;
      skill_match:   number;
      communication: number;
    },
    feedback?: string
  ) => Promise<void>;

}

export const useActivityStore = create<ActivityState>((set, get) => ({
  activities: [],
  isLoading: false,
  error: null,
  lastFetched: null,

  /* ──────────────────────────────────────────────
     FETCH
     ──────────────────────────────────────────── */
  fetchActivities: async () => {
    const CACHE_DURATION = 30_000; // 30 s
    const now = Date.now();
    const { lastFetched } = get();

    if (lastFetched && now - lastFetched < CACHE_DURATION) return;

    set({ isLoading: true, error: null });

    try {
      const activities = await getActivities();
      set({ activities, lastFetched: now, isLoading: false });
    } catch (err) {
      set({
        error: err instanceof Error ? err.message : 'Failed to fetch activities',
        isLoading: false
      });
    }
  },

  /* ──────────────────────────────────────────────
     UPDATE
     ──────────────────────────────────────────── */
  updateActivity: async (activityId, updates) => {
    try {
      // optimistic
      set(state => ({
        activities: state.activities.map(a =>
          a.id === activityId ? { ...a, ...updates } : a
        )
      }));

      await updateActivityStatus(activityId, updates);
      get().fetchActivities();
    } catch (err) {
      set({
        error: err instanceof Error ? err.message : 'Failed to update activity'
      });
      get().fetchActivities();
    }
  },

  /* ──────────────────────────────────────────────
     ACCEPT
     ──────────────────────────────────────────── */
  acceptActivityRequest: async activityId => {
    try {
      set({ lastFetched: null });

      // optimistic
      set(state => ({
        activities: state.activities.map(a =>
          a.id === activityId ? { ...a, status: 'confirmed', direction: undefined } : a
        )
      }));

      await acceptActivity(activityId);
      await get().fetchActivities();
    } catch (err) {
      set({
        error: err instanceof Error ? err.message : 'Failed to accept activity'
      });
      get().fetchActivities();
    }
  },

  /* ──────────────────────────────────────────────
     DECLINE
     ──────────────────────────────────────────── */
  declineActivityRequest: async (activityId, reason) => {
    try {
      await declineActivity(activityId, reason);

      // optimistic
      set(state => ({
        activities: state.activities.map(a =>
          a.id === activityId ? { ...a, status: 'declined' } : a
        )
      }));

      get().fetchActivities();
    } catch (err) {
      set({
        error: err instanceof Error ? err.message : 'Failed to decline activity'
      });
      get().fetchActivities();
    }
  },

  /* ──────────────────────────────────────────────
     RATE  – new signature matches supabase/activities.ts
     ──────────────────────────────────────────── */
   /* ──────────────────────────────────────────────
     RATE  – converts field names & sends feedback
     ──────────────────────────────────────────── */
  rateActivityRequest: async (
    activityId: string,
    ratings: {
      punctuality:   number;
      sportsmanship: number;
      skill_match:   number;
      communication: number;
    },
    feedback?: string
  ) => {
    try {
      /* map to the keys expected by lib/supabase/activities.ts */
      await rateActivity(activityId, {
        rating_punctuality:   ratings.punctuality,
        rating_sportsmanship: ratings.sportsmanship,
        rating_skill_match:   ratings.skill_match,
        rating_communication: ratings.communication,
        rating_feedback:      feedback ?? null
      });

      /* optimistic UI flag */
      set(state => ({
        activities: state.activities.map(a =>
          a.id === activityId ? { ...a, isRated: true } : a
        )
      }));

      /* sync from backend */
      await get().fetchActivities();
    } catch (err) {
      set({
        error: err instanceof Error ? err.message : 'Failed to rate activity'
      });
      get().fetchActivities();
    }
  },

}));